/* Banks management — list of held banks (with derived balance), bank-to-bank
   transfers (with edit/delete), and per-bank Ledger tab. */
const { useState: useStateBk, useEffect: useEffectBk } = React;

function BanksPage() {
  const [editing, setEditing] = useStateBk(null);
  const [creating, setCreating] = useStateBk(false);
  const [transferring, setTransferring] = useStateBk(null); // null = closed, true = create, object = edit
  const [tab, setTab] = useStateBk('banks');

  const banks = QH_DATA.banks || [];
  const transfers = QH_DATA.bankTransfers || [];
  const totalBalance = banks.reduce((s, b) => s + Number(b.balance || 0), 0);
  const activeCount = banks.filter(b => b.active).length;

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb"><span>Directory</span><I.ChevronRight s={10}/><span>Banks</span></div>
          <h1>Banks</h1>
          <div className="subtitle">{activeCount} active banks · {fmtBHD(totalBalance)} BHD held across all banks</div>
        </div>
        <div className="page-header-actions">
          <button className="btn" onClick={() => window.QH_STORE.reload()} title="Reload data"><I.Refresh/></button>
          {window.can('banks','create') && banks.filter(b=>b.active).length >= 2 &&
            <button className="btn" onClick={() => setTransferring(true)}><I.ArrowRight/> Transfer funds</button>}
          {window.can('banks','create') &&
            <button className="btn primary" onClick={() => setCreating(true)}><I.Plus/> New bank</button>}
        </div>
      </div>
      <div className="page-body" style={{display:'flex',flexDirection:'column',gap:12}}>
        <div className="tabs" style={{borderRadius:'var(--radius) var(--radius) 0 0',border:'1px solid var(--line)',borderBottom:'none'}}>
          <button className={`tab ${tab === 'banks' ? 'active' : ''}`} onClick={() => setTab('banks')}>Banks<span className="count">{banks.length}</span></button>
          <button className={`tab ${tab === 'transfers' ? 'active' : ''}`} onClick={() => setTab('transfers')}>Transfers<span className="count">{transfers.length}</span></button>
          <button className={`tab ${tab === 'ledger' ? 'active' : ''}`} onClick={() => setTab('ledger')}>Bank Ledger</button>
        </div>

        {tab === 'banks' && (
          <div className="table-wrap" style={{borderTopLeftRadius:0,borderTopRightRadius:0,marginTop:-12}}>
            <table className="data">
              <thead><tr>
                <th>Name</th><th>Code</th><th>Branch</th><th>Account</th>
                <th className="right">Deposits in</th><th className="right">Withdrawals out</th>
                <th className="right">Net transfers</th><th className="right">Balance · BHD</th>
                <th>Status</th><th></th>
              </tr></thead>
              <tbody>
                {banks.map(b => (
                  <tr key={b.id} className="clickable" onClick={() => window.can('banks','edit') && setEditing(b)}>
                    <td><div style={{fontWeight:500}}>{b.name}</div>{b.notes && <div className="text-xs muted">{b.notes}</div>}</td>
                    <td className="font-mono text-xs">{b.code || '—'}</td>
                    <td className="text-xs">{b.branch || '—'}</td>
                    <td className="font-mono text-xs muted">{b.accountNumber || '—'}</td>
                    <td className="num right pos">+{fmtBHD(b.depositsIn)}</td>
                    <td className="num right neg">−{fmtBHD(b.withdrawalsOut)}</td>
                    <td className="num right">{(b.transfersIn - b.transfersOut) >= 0 ? '+' : ''}{fmtBHD(b.transfersIn - b.transfersOut)}</td>
                    <td className="num right" style={{fontWeight:600}}>{fmtBHD(b.balance)}</td>
                    <td><span className={`tag ${b.active ? 'pos' : ''}`}><span className="dot"/>{b.active ? 'active' : 'inactive'}</span></td>
                    <td><I.ChevronRight s={12}/></td>
                  </tr>
                ))}
                {banks.length === 0 && (
                  <tr><td colSpan={10} style={{textAlign:'center',padding:24,color:'var(--ink-3)',fontSize:12}}>No banks yet — add one to start recording transactions and transfers.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'transfers' && (
          <div className="table-wrap" style={{borderTopLeftRadius:0,borderTopRightRadius:0,marginTop:-12}}>
            <table className="data">
              <thead><tr>
                <th>Date</th><th>From</th><th>To</th><th className="right">Amount · BHD</th>
                <th>Reference</th><th>Note</th><th>By</th><th style={{width:32}} title="Attached documents"><I.Paperclip s={11}/></th><th></th>
              </tr></thead>
              <tbody>
                {transfers.map(t => (
                  <tr key={t.id}>
                    <td className="font-mono text-xs">{window.fmtDateTime(t.date)}</td>
                    <td>{t.fromBankName}</td>
                    <td>{t.toBankName}</td>
                    <td className="num right" style={{fontWeight:600}}>{fmtBHD(t.amount)}</td>
                    <td className="font-mono text-xs muted">{t.reference || '—'}</td>
                    <td className="text-xs">{t.note || '—'}</td>
                    <td className="text-xs muted">{t.operator}</td>
                    <td style={{textAlign:'center'}}>
                      {t.documentCount > 0 ? (
                        <button className="icon-btn" title={`${t.documentCount} document(s) — open transfer to view`} onClick={() => setTransferring(t)}>
                          <I.Paperclip s={11}/>
                        </button>
                      ) : <span className="muted text-xs">—</span>}
                    </td>
                    <td style={{whiteSpace:'nowrap',textAlign:'right'}}>
                      {window.can('banks','edit') && <button className="icon-btn" title="Edit transfer" onClick={() => setTransferring(t)}><I.Settings s={12}/></button>}
                      {window.can('banks','delete') && <button className="icon-btn" title="Delete transfer" style={{color:'var(--neg)'}} onClick={async () => {
                        if (!window.confirm(`Delete transfer of ${fmtBHD(t.amount)} BHD from ${t.fromBankName} to ${t.toBankName}? This cannot be undone.`)) return;
                        try { await window.QH_API.delete('/banks/transfers/' + t.id); await window.QH_STORE.reload(); }
                        catch (e) { alert(e.message); }
                      }}><I.X s={12}/></button>}
                    </td>
                  </tr>
                ))}
                {transfers.length === 0 && (
                  <tr><td colSpan={8} style={{textAlign:'center',padding:24,color:'var(--ink-3)',fontSize:12}}>No transfers recorded yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'ledger' && <BankLedgerTab/>}
      </div>
      {creating && <BankModal onClose={() => setCreating(false)}/>}
      {editing && <BankModal bank={editing} onClose={() => setEditing(null)}/>}
      {transferring && (
        <BankTransferModal
          transfer={typeof transferring === 'object' ? transferring : null}
          onClose={() => setTransferring(null)}/>
      )}
      {/* On the action header the create button passes `true`; row edit passes the row. */}
    </>
  );
}

function BankModal({ bank, onClose }) {
  const isEdit = !!bank;
  const [form, setForm] = useStateBk({
    name: bank?.name || '',
    code: bank?.code || '',
    branch: bank?.branch || '',
    accountNumber: bank?.accountNumber || '',
    notes: bank?.notes || '',
    active: bank?.active ?? true,
  });
  const [busy, setBusy] = useStateBk(false);
  const [err, setErr] = useStateBk('');
  const set = (k) => (e) => setForm(f => ({...f, [k]: e.target.value}));

  const submit = async () => {
    if (!form.name.trim()) { setErr('Bank name is required.'); return; }
    setBusy(true); setErr('');
    try {
      const body = {
        name: form.name.trim(), code: form.code || null, branch: form.branch || null,
        accountNumber: form.accountNumber || null, notes: form.notes || null,
      };
      if (isEdit) await window.QH_API.put(`/banks/${bank.id}`, { ...body, active: form.active });
      else await window.QH_API.post('/banks', body);
      await window.QH_STORE.reload();
      onClose();
    } catch (e) { setErr(e.message || 'Save failed.'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete or deactivate bank "${bank.name}"?`)) return;
    setBusy(true);
    try { await window.QH_API.delete(`/banks/${bank.id}`); await window.QH_STORE.reload(); onClose(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>{isEdit ? `Edit bank — ${bank.name}` : 'New bank'}</h3><button className="icon-btn close" onClick={onClose}><I.X/></button></div>
        <div className="modal-body" style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <div className="field" style={{gridColumn:'1 / -1'}}><label>Name *</label><input className="input" value={form.name} onChange={set('name')} placeholder="e.g. Bank ABC" autoFocus={!isEdit}/></div>
          <div className="field"><label>Code</label><input className="input font-mono" value={form.code} onChange={set('code')} placeholder="ABC"/></div>
          <div className="field"><label>Branch</label><input className="input" value={form.branch} onChange={set('branch')} placeholder="Manama main"/></div>
          <div className="field" style={{gridColumn:'1 / -1'}}><label>Account number</label><input className="input font-mono" value={form.accountNumber} onChange={set('accountNumber')}/></div>
          <div className="field" style={{gridColumn:'1 / -1'}}><label>Notes</label><textarea className="textarea" value={form.notes} onChange={set('notes')}/></div>
          {isEdit && (
            <div className="field"><label>Status</label>
              <select className="select" value={form.active ? '1' : '0'} onChange={e => setForm(f => ({...f, active: e.target.value === '1'}))}>
                <option value="1">Active</option><option value="0">Inactive</option>
              </select>
            </div>
          )}
          {err && <div style={{gridColumn:'1 / -1',color:'var(--neg)',fontSize:12}}>{err}</div>}
        </div>
        <div className="modal-footer">
          {isEdit && window.can('banks','delete') && <button className="btn danger" onClick={remove} disabled={busy}><I.X/> Delete / deactivate</button>}
          <div style={{flex:1}}/>
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn primary" onClick={submit} disabled={busy}><I.Check/> {busy ? 'Saving…' : (isEdit ? 'Save' : 'Create')}</button>
        </div>
      </div>
    </div>
  );
}

function BankTransferModal({ transfer, onClose }) {
  const isEdit = !!transfer;
  // For edit, also include inactive banks if the transfer references one
  // (so the existing FK is still selectable in the dropdown).
  const allBanks = QH_DATA.banks || [];
  const banks = allBanks.filter(b => b.active
    || (transfer && (b.id === transfer.fromBankId || b.id === transfer.toBankId)));
  const [fromId, setFromId] = useStateBk(transfer?.fromBankId || banks[0]?.id || '');
  const [toId, setToId] = useStateBk(transfer?.toBankId || banks.filter(b => b.id !== (transfer?.fromBankId || banks[0]?.id))[0]?.id || '');
  const [amount, setAmount] = useStateBk(transfer ? String(transfer.amount) : '');
  const [date, setDate] = useStateBk(
    (transfer?.date && String(transfer.date).slice(0, 10)) ||
    new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useStateBk(transfer?.reference || '');
  const [note, setNote] = useStateBk(transfer?.note || '');
  const [busy, setBusy] = useStateBk(false);
  const [err, setErr] = useStateBk('');
  const docsRef = React.useRef(null);

  const fromBank = allBanks.find(b => b.id === fromId);
  const toBank = allBanks.find(b => b.id === toId);
  const amountNum = Number(amount);
  const sameBank = fromId && toId && fromId === toId;
  // For edit, the original transfer is already part of the displayed balance,
  // so add it back when comparing against amount (to avoid false positives).
  const fromBankBalance = fromBank ? fromBank.balance + (isEdit && transfer.fromBankId === fromId ? Number(transfer.amount) : 0) : 0;
  const insufficientFunds = fromBank && amountNum > fromBankBalance;
  const valid = fromId && toId && !sameBank && amountNum > 0;

  const submit = async () => {
    if (!fromId || !toId) { setErr('Pick both source and destination banks.'); return; }
    if (sameBank) { setErr('Source and destination must be different.'); return; }
    if (!(amountNum > 0)) { setErr('Amount must be a positive number.'); return; }
    setBusy(true); setErr('');
    try {
      const body = {
        fromBankId: fromId, toBankId: toId, amount: amountNum,
        date: date || null,
        reference: reference || null, note: note || null,
      };
      let savedId = transfer?.id;
      if (isEdit) await window.QH_API.put('/banks/transfers/' + transfer.id, body);
      else {
        const created = await window.QH_API.post('/banks/transfers', body);
        savedId = created?.id;
      }
      // Flush any docs the operator picked before the transfer existed.
      // Failure here doesn't unwind the transfer (it's saved); surface
      // the error to the operator so they can retry from the edit modal.
      if (docsRef.current?.hasStaged() && savedId) {
        try { await docsRef.current.flushStaged(savedId); }
        catch (e) { setErr('Transfer saved but document upload failed: ' + (e.message || 'unknown')); setBusy(false); return; }
      }
      await window.QH_STORE.reload();
      onClose();
    } catch (e) { setErr(e.message || 'Transfer failed.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>{isEdit ? 'Edit transfer' : 'Transfer funds between banks'}</h3><button className="icon-btn close" onClick={onClose}><I.X/></button></div>
        <div className="modal-body" style={{display:'flex',flexDirection:'column',gap:12}}>
          <div className="field" style={{maxWidth:200}}>
            <label>Date</label>
            <input className="input font-mono" type="date" value={date} onChange={e => setDate(e.target.value)}/>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr auto 1fr',gap:8,alignItems:'end'}}>
            <div className="field"><label>From bank</label>
              <select className="select" value={fromId} onChange={e => setFromId(e.target.value)}>
                <option value="">— Select —</option>
                {banks.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              {fromBank && <div className="text-xs muted" style={{marginTop:4}}>Balance: <span className="font-mono">{fmtBHD(fromBank.balance)}</span> BHD</div>}
            </div>
            <div style={{paddingBottom:18,color:'var(--ink-3)'}}><I.ArrowRight/></div>
            <div className="field"><label>To bank</label>
              <select className="select" value={toId} onChange={e => setToId(e.target.value)}>
                <option value="">— Select —</option>
                {banks.filter(b => b.id !== fromId).map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
              {toBank && <div className="text-xs muted" style={{marginTop:4}}>Balance: <span className="font-mono">{fmtBHD(toBank.balance)}</span> BHD</div>}
            </div>
          </div>
          <div className="field"><label>Amount (BHD)</label>
            <input className="input font-mono" value={amount} onChange={e => setAmount(e.target.value)}
                   placeholder="0.000" style={{fontSize:18,fontWeight:600}}/>
            {insufficientFunds && <div className="text-xs" style={{color:'var(--warn)',marginTop:4}}>
              <I.AlertCircle s={11}/> Amount exceeds {fromBank.name} balance — proceed only if the bank can fund it externally.
            </div>}
          </div>
          <div className="field"><label>Reference (optional)</label><input className="input font-mono" value={reference} onChange={e => setReference(e.target.value)} placeholder="e.g. TRF-2026-001"/></div>
          <div className="field"><label>Note (optional)</label><textarea className="textarea" value={note} onChange={e => setNote(e.target.value)} placeholder="Reason for the transfer"/></div>
          <window.DocumentAttachments ref={docsRef}
            entityType="bank_transfer"
            entityId={transfer?.id || null}
            editable={window.can('banks', isEdit ? 'edit' : 'create')}/>
          {err && <div style={{color:'var(--neg)',fontSize:12}}>{err}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <div style={{flex:1}}/>
          <button className="btn primary" onClick={submit} disabled={busy || !valid}>
            <I.Check/> {busy ? (isEdit ? 'Saving…' : 'Transferring…') : (isEdit ? `Save (${amount ? fmtBHD(amountNum) : '0'} BHD)` : `Transfer ${amount ? fmtBHD(amountNum) : '0'} BHD`)}
          </button>
        </div>
      </div>
    </div>
  );
}

// Bank Ledger — every cash movement (deposits, withdrawals, transfers in/out)
// for a chosen bank in a date range, with opening + running balance.
function BankLedgerTab() {
  const banks = (QH_DATA.banks || []).filter(b => b.active);
  const [bankId, setBankId] = useStateBk(banks[0]?.id || '');
  // Default range: last 4 months back to today (inclusive).
  const fmtDay = (d) => d.toISOString().slice(0, 10);
  const today = new Date();
  const fourMonthsAgo = new Date(today); fourMonthsAgo.setMonth(today.getMonth() - 4);
  const [from, setFrom] = useStateBk(fmtDay(fourMonthsAgo));
  const [to, setTo] = useStateBk(fmtDay(today));
  const [data, setData] = useStateBk(null);
  const [busy, setBusy] = useStateBk(false);
  const [err, setErr] = useStateBk('');

  useEffectBk(() => {
    if (!bankId || !from || !to) { setData(null); return; }
    let cancel = false;
    setBusy(true); setErr('');
    window.QH_API.get(`/banks/${bankId}/ledger?from=${from}&to=${to}`)
      .then(r => { if (!cancel) setData(r); })
      .catch(e => { if (!cancel) setErr(e.message || 'Failed to load ledger.'); })
      .finally(() => { if (!cancel) setBusy(false); });
    return () => { cancel = true; };
  }, [bankId, from, to]);

  const exportCsv = () => {
    if (!data) return;
    const rows = [['Date', 'Type', 'Description', 'Reference', 'In · BHD', 'Out · BHD', 'Balance · BHD']];
    rows.push(['(opening)', '', '', '', '', '', String(data.opening)]);
    for (const e of data.entries)
      rows.push([e.date, e.type, e.description, e.reference, e.in || '', e.out || '', String(e.balance)]);
    rows.push(['(closing)', '', '', '', String(data.totalIn), String(data.totalOut), String(data.closing)]);
    const csv = rows.map(r => r.map(x => /[",\n]/.test(String(x)) ? `"${String(x).replace(/"/g, '""')}"` : x).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `ledger-${data.bankName.replace(/\s+/g, '_')}-${from}_to_${to}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  return (
    <div style={{display:'flex',flexDirection:'column',gap:12}}>
      <div className="toolbar" style={{borderRadius:'var(--radius) var(--radius) 0 0',border:'1px solid var(--line)',borderBottom:'none',marginTop:-12}}>
        <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:'var(--ink-2)'}}>
          Bank
          <select className="select" style={{height:28,minWidth:200}} value={bankId} onChange={e => setBankId(e.target.value)}>
            <option value="">— Select bank —</option>
            {banks.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </label>
        <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:'var(--ink-2)'}}>
          From
          <input className="input font-mono" style={{height:28,width:140}} type="date" value={from} onChange={e => setFrom(e.target.value)}/>
        </label>
        <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,color:'var(--ink-2)'}}>
          To
          <input className="input font-mono" style={{height:28,width:140}} type="date" value={to} onChange={e => setTo(e.target.value)}/>
        </label>
        <button className="btn sm ghost" onClick={() => {
          const t = new Date(); const f = new Date(); f.setMonth(t.getMonth() - 4);
          setFrom(fmtDay(f)); setTo(fmtDay(t));
        }}>Last 4 months</button>
        <div className="grow"/>
        {data && <button className="btn sm" onClick={exportCsv}><I.Download/> Export CSV</button>}
      </div>

      {!bankId && (
        <div style={{padding:24,textAlign:'center',color:'var(--ink-3)',fontSize:13,border:'1px solid var(--line)',borderTop:'none'}}>
          Pick a bank to view its ledger.
        </div>
      )}
      {bankId && busy && (
        <div style={{padding:24,textAlign:'center',color:'var(--ink-3)',fontSize:13,border:'1px solid var(--line)',borderTop:'none'}}>Loading…</div>
      )}
      {bankId && err && !busy && (
        <div style={{padding:14,color:'var(--neg)',fontSize:13,border:'1px solid var(--line)',borderTop:'none'}}>{err}</div>
      )}
      {bankId && data && !busy && <>
        <KPIStrip items={[
          { label: 'Opening balance',  value: fmtBHD(data.opening) + ' BHD',  sub: `Before ${window.fmtDate(data.from)}` },
          { label: 'Total in',         value: '+' + fmtBHD(data.totalIn) + ' BHD',  sub: `${data.entries.filter(e => e.in > 0).length} entries` },
          { label: 'Total out',        value: '−' + fmtBHD(data.totalOut) + ' BHD', sub: `${data.entries.filter(e => e.out > 0).length} entries` },
          { label: 'Closing balance',  value: fmtBHD(data.closing) + ' BHD',  sub: data.bankName },
        ]}/>
        <div className="table-wrap" style={{maxHeight:540,overflow:'auto'}}>
          <table className="data">
            <thead style={{position:'sticky',top:0,background:'var(--bg-elev)',zIndex:1}}>
              <tr>
                <th style={{width:32}} title="Reconciled?"><I.Check s={11}/></th>
                <th>Date</th><th>Type</th><th>Description</th><th>Reference</th>
                <th className="right">In</th><th className="right">Out</th><th className="right">Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{background:'var(--bg-sunken)'}}>
                <td/>
                <td colSpan={6} className="text-xs muted" style={{fontStyle:'italic'}}>Opening balance carried forward</td>
                <td className="num right" style={{fontWeight:600}}>{fmtBHD(data.opening)}</td>
              </tr>
              {data.entries.map((e, i) => (
                <LedgerRowView key={i} bankId={bankId} row={e}
                  onReconciled={(rec) => setData(d => d ? ({
                    ...d, entries: d.entries.map((x, j) => j === i ? { ...x, reconciled: rec } : x),
                  }) : d)}/>
              ))}
              {data.entries.length === 0 && (
                <tr><td colSpan={8} style={{textAlign:'center',padding:24,color:'var(--ink-3)',fontSize:12}}>No movements in this date range.</td></tr>
              )}
              <tr style={{background:'var(--bg-sunken)'}}>
                <td/>
                <td colSpan={4} style={{fontWeight:600}}>Closing balance</td>
                <td className="num right pos" style={{fontWeight:600}}>+{fmtBHD(data.totalIn)}</td>
                <td className="num right neg" style={{fontWeight:600}}>−{fmtBHD(data.totalOut)}</td>
                <td className="num right" style={{fontWeight:700,fontSize:14}}>{fmtBHD(data.closing)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </>}
    </div>
  );
}

// One ledger row with its own reconciled state — flipping the checkbox
// hits the server and optimistically updates the parent.
function LedgerRowView({ bankId, row, onReconciled }) {
  const [busy, setBusy] = useStateBk(false);
  const toggle = async (e) => {
    const next = e.target.checked;
    setBusy(true);
    try {
      await window.QH_API.put(`/banks/${bankId}/ledger/reconcile`, {
        sourceType: row.sourceType, sourceId: row.sourceId,
        reconciled: next,
        reconciledOn: new Date().toISOString().slice(0,10),
      });
      onReconciled?.(next);
    } catch (err) { alert(err.message || 'Failed to update.'); }
    finally { setBusy(false); }
  };
  const tone = row.type === 'deposit' || row.type === 'transfer-in' || row.type === 'repayment-cleared' ? 'pos' : 'neg';
  return (
    <tr style={row.reconciled ? { background: 'var(--pos-soft)', opacity: 0.85 } : undefined}>
      <td style={{textAlign:'center'}}>
        <input type="checkbox" checked={!!row.reconciled} onChange={toggle} disabled={busy}
          title={row.reconciled ? 'Reconciled — click to undo' : 'Mark reconciled against bank statement'}/>
      </td>
      <td className="font-mono text-xs">{window.fmtDate(row.date)}</td>
      <td><span className={`tag ${tone}`} style={{textTransform:'none'}}>{row.type}</span></td>
      <td>{row.description}</td>
      <td className="font-mono text-xs muted">{row.reference}</td>
      <td className="num right pos">{row.in > 0 ? '+' + fmtBHD(row.in) : ''}</td>
      <td className="num right neg">{row.out > 0 ? '−' + fmtBHD(row.out) : ''}</td>
      <td className="num right" style={{fontWeight:600}}>{fmtBHD(row.balance)}</td>
    </tr>
  );
}

window.BanksPage = BanksPage;
window.BankLedgerTab = BankLedgerTab;
window.BankModal = BankModal;
window.BankTransferModal = BankTransferModal;
